import { z } from "zod";
import { authResultSchema } from "./auth.js";
import { defineEndpoint } from "./endpoint.js";
/**
 * "Try it yourself" (docs/specs/sandbox.md, SB-03): a visitor claims a ready-made demo business and enters its Admin
 * with a one-time code. Both routes exist only on a sandbox instance (404 elsewhere).
 */
export const claimSandboxRequestSchema = z.object({
    /** The Cloudflare Turnstile widget's answer. */
    turnstileToken: z.string().min(1).max(2048),
    locale: z.enum(["en", "bn"]).default("en"),
});
export const claimSandboxResponseSchema = z.object({
    /** One-time, valid 2 minutes: the Admin exchanges it for a session (`POST /v1/sandbox/enter`). */
    enterCode: z.string(),
    /** The Admin page that takes the code (from its URL fragment, so it never reaches a server log). */
    adminUrl: z.url(),
    /** The browser POS, once it exists (SB-04); null until then. */
    posUrl: z.url().nullable(),
    storefrontUrl: z.url(),
    /** When this demo business stops working: 24 h, or the nightly reset if that comes first. */
    expiresAt: z.iso.datetime(),
});
/** 32 random bytes, base64url. */
export const sandboxEnterCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const enterSandboxRequestSchema = z.object({ code: sandboxEnterCodeSchema });
const sandbox = { tag: "sandbox" };
export const claimSandbox = defineEndpoint({
    ...sandbox,
    name: "claimSandbox",
    method: "POST",
    path: "/v1/sandbox/sessions",
    access: { public: true },
    summary: "Take a ready demo business (bot check, a few per IP and day); returns the one-time code to enter it",
    body: claimSandboxRequestSchema,
    status: 201,
    response: claimSandboxResponseSchema,
    errors: ["BOT_CHECK_FAILED", "RATE_LIMITED", "SANDBOX_BUSY"],
});
export const enterSandbox = defineEndpoint({
    ...sandbox,
    name: "enterSandbox",
    method: "POST",
    path: "/v1/sandbox/enter",
    access: { public: true },
    summary: "Exchange the one-time code for a signed-in session as the demo business's Owner (sets the refresh cookie)",
    body: enterSandboxRequestSchema,
    status: 200,
    response: authResultSchema,
});
// SB-05: the guided checklist ---------------------------------------------------------------------------------------
/** The five things to try, in order (docs/specs/sandbox.md "Frontends"). */
export const SANDBOX_CHECKLIST_STEPS = [
    "pos_sale",
    "offline_sale",
    "online_order",
    "courier_booked",
    "report_viewed",
];
export const sandboxProgressSchema = z.object({
    steps: z.array(z.object({
        step: z.enum(SANDBOX_CHECKLIST_STEPS),
        completedAt: z.iso.datetime().nullable(),
    })),
    /** When this demo business stops working. */
    expiresAt: z.iso.datetime().nullable(),
});
export const getSandboxProgress = defineEndpoint({
    ...sandbox,
    name: "getSandboxProgress",
    method: "GET",
    path: "/v1/sandbox/progress",
    access: { permission: "dashboard.view" },
    summary: "The demo business's checklist: which of the five steps the visitor has done, and when the demo ends",
    status: 200,
    response: sandboxProgressSchema,
});
export const markSandboxReportViewed = defineEndpoint({
    ...sandbox,
    name: "markSandboxReportViewed",
    method: "POST",
    path: "/v1/sandbox/progress/report-viewed",
    access: { permission: "dashboard.view" },
    summary: "The Admin opened a report in the demo business (the checklist's last step)",
    status: 204,
});
//# sourceMappingURL=sandbox.js.map